import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, Quote, Star } from 'lucide-react';
import DecorLeaf from './DecorLeaf';
import { homeRail, focusRing } from '../styles/ui';

// The only customer facts this project actually holds: a name, how the person
// describes themselves, their words and a 5-star rating. The reference card
// also shows a portrait, a city and a "Verified Customer" badge — none exists
// as data here, so none is rendered: a stock face or a verification claim
// would be fabricated social proof. The avatar is the reviewer's initials.
const REVIEWS = [
  {
    text: "The quality of the chicken and mutton is outstanding! Every cut is so fresh and tender. I've never found better meat anywhere else!",
    name: 'Priya Sharma',
    role: 'Regular Customer',
    initials: 'PS',
    rating: 5,
  },
  {
    text: 'Best meat shop in the area! The cuts are always fresh, prices are honest, and the staff is incredibly friendly. Feels like family!',
    name: 'Rajesh Kumar',
    role: 'Loyal Customer',
    initials: 'RK',
    rating: 5,
  },
  {
    text: 'Fresh & Homely truly delivers on freshness! The seafood selection is amazing and everything is so hygienically packed. Highly recommend!',
    name: 'Anita Desai',
    role: 'Happy Customer',
    initials: 'AD',
    rating: 5,
  },
  {
    text: "Their fish is incredibly fresh! You can tell it's premium quality. The cleanliness and packaging standards are exceptional.",
    name: 'Sneha Rao',
    role: 'Regular Shopper',
    initials: 'SR',
    rating: 5,
  },
  {
    text: 'Affordable prices for such high-quality meat and seafood. The attention to hygiene and freshness is impressive. Very reliable!',
    name: 'Vikram Singh',
    role: 'Health Enthusiast',
    initials: 'VS',
    rating: 5,
  },
  {
    text: 'The team is always welcoming and helpful! The meat is always fresh and perfectly cut. This is my go-to meat shop now!',
    name: 'Meera K.',
    role: 'Local Resident',
    initials: 'MK',
    rating: 5,
  },
];

// How many cards share the rail at each width. Five only where five can be read
// comfortably; a phone gets one whole card rather than five slivers.
export const visibleCountForWidth = (width) => {
  if (width >= 1600) return 5;
  if (width >= 1280) return 5;
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
};

const useVisibleCount = () => {
  const [count, setCount] = useState(() =>
    visibleCountForWidth(typeof window === 'undefined' ? 1280 : window.innerWidth)
  );
  useEffect(() => {
    const onResize = () => setCount(visibleCountForWidth(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return count;
};

const GAP_PX = 8;

const Avatar = ({ initials, className }) => (
  <span
    className={`rounded-full bg-[linear-gradient(135deg,#DFF3DA,#94D485)] text-emerald-900 flex items-center justify-center font-bold ring-2 ring-white shadow-soft shrink-0 ${className}`}
    aria-hidden="true"
  >
    {initials}
  </span>
);

const Testimonials = () => {
  const visible = useVisibleCount();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(REVIEWS.length / visible));
  // A narrower viewport means more pages, a wider one fewer, so the current
  // page has to be pulled back into range whenever the count changes.
  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  const goTo = useCallback(
    (next) => setPage(((next % pageCount) + pageCount) % pageCount),
    [pageCount]
  );
  const prev = useCallback(() => goTo(page - 1), [goTo, page]);
  const next = useCallback(() => goTo(page + 1), [goTo, page]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      next();
    }
  };

  // Touch swipe, since the arrows are hidden on phones and dots alone are a
  // poor primary control there.
  const touchStartX = React.useRef(null);
  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0].clientX;
  };
  const onTouchEnd = (event) => {
    if (touchStartX.current == null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) next();
    else prev();
  };

  const cardStyle = useMemo(
    () => ({ flex: `0 0 calc(${100 / visible}% - ${((visible - 1) * GAP_PX) / visible}px)` }),
    [visible]
  );

  const arrowClass = `absolute top-[82px] z-20 w-11 h-11 rounded-full bg-white border border-emerald-50 shadow-[0_8px_20px_-8px_rgba(15,51,24,0.35)] text-emerald-950 items-center justify-center transition-colors hover:bg-emerald-50 ${focusRing}`;

  return (
    <section
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="relative overflow-hidden pt-5 pb-5 lg:pt-6 lg:pb-6 bg-[linear-gradient(180deg,#FFFFFF_0%,#FAFCF8_100%)]"
    >
      {/* The design's edge artwork: a family with their groceries on the left,
          produce fading in on the right, a handwritten note and a few leaves.
          Decoration only. */}
      <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
        <img src="/home/testimonial-family.webp" alt="" loading="lazy" width="300" height="385" className="hidden xl:block absolute left-0 top-[19px] w-[300px]" />
        <img src="/home/testimonial-right.webp" alt="" loading="lazy" width="138" height="320" className="hidden xl:block absolute right-0 top-[124px] w-[138px]" />
        <img src="/home/note-real-people.png" alt="" loading="lazy" width="150" height="120" className="hidden xl:block absolute right-[106px] top-[24px] w-[150px]" />
        <DecorLeaf className="hidden xl:block absolute left-[290px] top-[110px] w-8 text-emerald-500/80 rotate-[30deg]" />
        <DecorLeaf className="hidden xl:block absolute right-[52px] top-[84px] w-9 text-emerald-500/80 -rotate-[20deg]" />
        <DecorLeaf className="hidden xl:block absolute right-[40px] bottom-[10px] w-10 text-emerald-600/70 rotate-[12deg]" />
      </div>

      <div className={`${homeRail} relative`}>
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1.5 h-[27px] px-4 rounded-full bg-[#FDEDEA] border border-[#F8D5CF] text-[#C2410C] text-[10.5px] font-bold tracking-[0.12em] uppercase">
            <Heart size={11} fill="currentColor" aria-hidden="true" />
            Loved By Our Community
          </span>
          <h2 id="testimonials-heading" className="serif mt-[13px] text-[32px] sm:text-[40px] xl:text-[44px] font-bold text-emerald-950 leading-[1.05]">
            What Our <span className="text-[#1E8A3B]">Families</span> Say
          </h2>
          <p className="mt-[12px] text-[15px] xl:text-[16px] text-[#4B5563]">
            Real stories. Real freshness. Real happiness from our valued customers.
          </p>
        </div>

        <div
          className="relative mt-[24px] xl:px-[29px]"
          role="group"
          aria-roledescription="carousel"
          aria-label="Customer reviews"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button type="button" onClick={prev} aria-label="Previous reviews" aria-controls="testimonial-rail" className={`${arrowClass} hidden sm:flex left-[-8px]`}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>

          <div className="overflow-hidden">
            <div
              id="testimonial-rail"
              aria-live="polite"
              className="flex items-stretch transition-transform duration-500 ease-out"
              style={{ gap: GAP_PX, transform: `translateX(calc(-${page * 100}% - ${page * GAP_PX}px))` }}
            >
              {REVIEWS.map((review) => (
                <figure
                  key={review.name}
                  style={cardStyle}
                  className="relative flex flex-col bg-white border border-[#E6EEE6] rounded-2xl shadow-[0_10px_24px_-14px_rgba(15,51,24,0.25)] px-[15px] pt-[8px] pb-[14px] min-h-[218px]"
                >
                  <Quote size={30} className="absolute right-4 top-4 text-emerald-100 rotate-180" strokeWidth={1} fill="currentColor" aria-hidden="true" />
                  <div className="flex items-center gap-[26px]">
                    <Avatar initials={review.initials} className="w-[62px] h-[62px] text-lg" />
                    <span className="flex gap-0.5 text-[#F5A623]" role="img" aria-label={`Rated ${review.rating} out of 5`}>
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <Star key={i} size={14} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                      ))}
                    </span>
                  </div>

                  <blockquote className="mt-[6px] pl-3 text-[12.5px] text-[#4B5563] leading-[1.45]">
                    <span className="text-emerald-300" aria-hidden="true">“ </span>
                    {review.text}
                    <span className="text-emerald-300" aria-hidden="true"> ”</span>
                  </blockquote>

                  <figcaption className="mt-auto pt-3 flex items-center gap-3">
                    <Avatar initials={review.initials} className="w-9 h-9 text-[11px]" />
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-bold text-emerald-950 leading-tight">{review.name}</span>
                      <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#1E7A3C]">
                        <DecorLeaf className="w-3" />
                        {review.role}
                      </span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <button type="button" onClick={next} aria-label="Next reviews" aria-controls="testimonial-rail" className={`${arrowClass} hidden sm:flex right-[-8px]`}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-[18px]">
          {Array.from({ length: pageCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`Go to review page ${index + 1} of ${pageCount}`}
              aria-current={index === page ? 'true' : undefined}
              className={`h-2 rounded-full transition-all ${focusRing} ${index === page ? 'w-5 bg-[#2E8B47]' : 'w-2 bg-[#D9E3D9] hover:bg-emerald-300'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
